import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useSecurityPin } from '../../context/SecurityPinContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  ScrollText, Search, RefreshCw, Filter, ShieldAlert,
} from 'lucide-react';

const ALL = '__all__';

const roleBadgeClass = (role) => {
  switch (role) {
    case 'admin': return 'bg-primary/10 text-primary border-primary/20';
    case 'manager': return 'bg-pos-occupied/10 text-pos-occupied border-pos-occupied/20';
    default: return 'bg-muted text-muted-foreground';
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

/**
 * Visualizzatore Registro Attività (audit log). Protetto da PIN di sicurezza:
 * il caricamento usa runSensitive, che richiede il PIN quando necessario.
 */
export const AuditLogDialog = ({ open, onOpenChange, entityType = null, title }) => {
  const { t } = useTranslation();
  const { runSensitive } = useSecurityPin();
  const resolvedTitle = title ?? t('audit.title');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ actions: [], entities: [], actors: [] });
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState('');

  const [q, setQ] = useState('');
  const [fAction, setFAction] = useState(ALL);
  const [fActor, setFActor] = useState(ALL);
  const [fEntity, setFEntity] = useState(entityType || ALL);

  const load = useCallback(async () => {
    setLoading(true); setDenied('');
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (fAction !== ALL) params.action = fAction;
      if (fActor !== ALL) params.actor_id = fActor;
      if (fEntity !== ALL) params.entity_type = fEntity;
      const [logsRes, metaRes] = await Promise.all([
        runSensitive((cfg) => api.get('/audit', { ...cfg, params }), { titleText: t('audit.pinPrompt') }),
        runSensitive((cfg) => api.get('/audit/meta', cfg), { titleText: t('audit.pinPrompt') }),
      ]);
      setItems(logsRes.data || []);
      setMeta(metaRes.data || { actions: [], entities: [], actors: [] });
    } catch (e) {
      if (e?.message === 'cancelled') { onOpenChange(false); return; }
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) { setDenied(detail || t('audit.accessDenied')); }
      else { toast.error(detail || t('audit.loadError')); }
    } finally { setLoading(false); }
  }, [q, fAction, fActor, fEntity, runSensitive, onOpenChange, t]);

  useEffect(() => {
    if (open) { setFEntity(entityType || ALL); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Ricarica quando cambiano i filtri (dialog aperto).
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => { load().catch(() => {}); }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, fAction, fActor, fEntity]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl" data-testid="audit-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-primary" /> {resolvedTitle}
          </DialogTitle>
          <DialogDescription>{t('audit.description')}</DialogDescription>
        </DialogHeader>

        {denied ? (
          <div className="p-8 text-center text-muted-foreground" data-testid="audit-denied">
            <ShieldAlert className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <div className="font-medium">{denied}</div>
          </div>
        ) : (
          <>
            {/* Filtri */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-10" placeholder={t('common.searchDots')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" />
              </div>
              <Select value={fEntity} onValueChange={setFEntity}>
                <SelectTrigger className="w-[160px]" data-testid="audit-filter-entity"><SelectValue placeholder={t('audit.type')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('audit.allTypes')}</SelectItem>
                  {meta.entities.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fAction} onValueChange={setFAction}>
                <SelectTrigger className="w-[190px]" data-testid="audit-filter-action"><SelectValue placeholder={t('audit.action')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('audit.allActions')}</SelectItem>
                  {meta.actions.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fActor} onValueChange={setFActor}>
                <SelectTrigger className="w-[170px]" data-testid="audit-filter-actor"><SelectValue placeholder={t('audit.operator')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('audit.allOperators')}</SelectItem>
                  {meta.actors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.username}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="rounded-xl" onClick={() => load()} data-testid="audit-refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Lista */}
            <ScrollArea className="h-[55vh] mt-2 pr-2">
              {loading && items.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">{t('common.loading')}</div>
              ) : items.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground" data-testid="audit-empty">
                  <Filter className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <div className="font-medium">{t('audit.emptyTitle')}</div>
                  <div className="text-sm">{t('audit.emptyDesc')}</div>
                </div>
              ) : (
                <div className="space-y-2" data-testid="audit-list">
                  {items.map((it) => (
                    <div key={it.id} className="rounded-xl border border-border p-3 flex items-start justify-between gap-3" data-testid={`audit-row-${it.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${roleBadgeClass(it.actor_role)}`}>
                            {t(`roles.${it.actor_role}`, { defaultValue: it.actor_role })}
                          </Badge>
                          <span className="font-medium text-sm">{it.actor_name || it.actor_username}</span>
                          <span className="text-xs text-muted-foreground">· {it.action_label}</span>
                        </div>
                        <div className="text-sm mt-1 break-words">{it.label}</div>
                        {it.note ? <div className="text-xs text-muted-foreground mt-0.5">{t('audit.note')}: {it.note}</div> : null}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(it.at)}</div>
                        {typeof it.amount === 'number' ? <div className="text-sm font-semibold mt-1">€ {Number(it.amount).toFixed(2)}</div> : null}
                        {typeof it.points === 'number' ? <div className="text-sm font-semibold mt-1">{it.points > 0 ? '+' : ''}{it.points} pt</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuditLogDialog;
